import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Bell, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 60_000;

const askPermissionOnce = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission === 'default') {
    Notification.requestPermission().catch(() => {});
  }
};

const showDesktopNotification = (notif) => {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  try {
    const n = new Notification(notif.title, {
      body: notif.message,
      icon: '/favicon.ico',
      tag: notif.id,
    });
    n.onclick = () => {
      window.focus();
      if (notif.link) window.location.href = notif.link;
      n.close();
    };
  } catch (err) {
    console.warn('[notifications] desktop notif failed', err);
  }
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const seenIdsRef = useRef(new Set());
  const dropdownRef = useRef(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const load = useCallback(async (opts = { announce: false }) => {
    try {
      const { data } = await axios.get(`${API}/api/notifications`, { withCredentials: true });
      const list = Array.isArray(data) ? data : [];
      // Detect brand-new unread notifications to fire toast + desktop notif
      if (opts.announce) {
        list.forEach((n) => {
          if (!n.read && !seenIdsRef.current.has(n.id)) {
            toast(n.title, { description: n.message });
            showDesktopNotification(n);
          }
        });
      }
      seenIdsRef.current = new Set(list.map((n) => n.id));
      setNotifications(list);
    } catch (err) {
      console.warn('[notifications] load failed', err);
    }
  }, []);

  useEffect(() => {
    askPermissionOnce();
    // Initial load — mark all current ids as "seen" so we don't spam on first mount
    load({ announce: false });
  }, [load]);

  useEffect(() => {
    const interval = setInterval(() => load({ announce: true }), POLL_INTERVAL_MS);
    const onFocus = () => load({ announce: true });
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const markOneRead = async (id) => {
    setNotifications((cur) => cur.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      await axios.patch(`${API}/api/notifications/${id}/read`, {}, { withCredentials: true });
    } catch (err) {
      console.warn('[notifications] mark read failed', err);
    }
  };

  const markAllRead = async () => {
    setNotifications((cur) => cur.map((n) => ({ ...n, read: true })));
    try {
      await axios.post(`${API}/api/notifications/mark-all-read`, {}, { withCredentials: true });
    } catch (err) {
      console.warn('[notifications] mark all read failed', err);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        data-testid="notif-bell-btn"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-full hover:bg-slate-100 text-[#1E3A5F] transition-all"
        aria-label="Notificaciones"
      >
        <Bell className="w-5 h-5" strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span
            data-testid="notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-[#D97706] text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-dropdown"
          className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-slate-100 z-50 overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <p className="font-bold text-[#1E3A5F]">Notificaciones</p>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  data-testid="notif-mark-all-read"
                  onClick={markAllRead}
                  className="text-xs text-[#1E3A5F] hover:bg-slate-100 px-2 py-1 rounded-full font-semibold flex items-center gap-1"
                >
                  <Check className="w-3.5 h-3.5" /> Todo leído
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded-full hover:bg-slate-100 text-slate-500"
                aria-label="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-10 h-10 text-slate-200 mx-auto mb-2" strokeWidth={1.5} />
                <p className="text-sm text-slate-500">Sin notificaciones por ahora</p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  onClick={() => {
                    if (!n.read) markOneRead(n.id);
                    if (n.link) window.location.href = n.link;
                  }}
                  className={`w-full text-left px-4 py-3 border-b border-slate-50 last:border-b-0 hover:bg-slate-50 transition-all ${
                    n.read ? 'bg-white' : 'bg-amber-100 dark:bg-amber-500/15/30'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                        n.read ? 'bg-slate-200' : 'bg-[#D97706]'
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-[#1E3A5F]">{n.title}</p>
                      <p className="text-xs text-slate-600 mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-slate-400 mt-1">
                        {new Date(n.created_at).toLocaleString('es-AR')}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
